import { format } from "date-fns";
import { maxBy } from "lodash-es";
import { FC, useMemo } from "react";

import { SessionStatsCard } from "./SessionStatsCard";

import type { ClassSessionType } from "components/server-types";
import { t } from "i18n/i18n";
import { CalendarIcon, ClockIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentCourseId } from "store/selectors";

type SessionsCardProps = {
  sessions: ClassSessionType[];
};

export const SessionsCard: FC<SessionsCardProps> = ({ sessions }) => {
  const courseId = useAppSelector(selectCurrentCourseId);
  const { canAuthorCourse } = useCapabilities();

  const sessionCount = useMemo(() => sessions.length, [sessions]);
  const lastSessionDateString = useMemo(() => {
    const lastSessionDate = maxBy(sessions, "started_at")?.started_at;
    return lastSessionDate ? format(new Date(lastSessionDate), "MMM d, yyyy") : "";
  }, [sessions]);

  if (!canAuthorCourse || !courseId) return null;

  const lastSessionText =
    sessionCount > 0
      ? t("session_log_view.stats.sessions.last_session", {
          date: lastSessionDateString,
        })
      : t("session_log_view.stats.sessions.no_sessions");

  return (
    <SessionStatsCard
      FooterIcon={ClockIcon}
      footerText={lastSessionText}
      kind="sessions"
      title={t("session_log_view.stats.sessions.title")}
      TitleIcon={CalendarIcon}
      value={sessionCount}
      valueDescription={t("session_log_view.stats.sessions.value_description")}
    />
  );
};
